/**
 * CursorContext — given an LSP-style position (0-based line, 0-based
 * character), returns the qualified name under the cursor (if any),
 * the token there, and the containing AST node.
 *
 * Used by hover, definition, references, rename, completion.
 */

import type { SourceSpan } from "@archlang/engine";
import type { Token } from "@archlang/engine";
import { RESERVED_KEYWORDS } from "@archlang/engine";
import type { Workspace } from "./workspace.js";

/** v0.8.2: a reserved word can be a NAME, so treat a keyword token like an
 *  identifier when resolving a cursor / reference (`rest_read select`). */
function isNameToken(tok: Token): boolean {
  return tok.type === "IDENTIFIER" || RESERVED_KEYWORDS.has(tok.value);
}

export interface CursorHit {
  readonly text: string;
  readonly span: SourceSpan;
}

export class CursorContext {
  constructor(private readonly workspace: Workspace) {}

  qualifiedNameAt(uri: string, line: number, character: number): CursorHit | null {
    const file = this.workspace.getFile(uri);
    if (!file) return null;

    const offset = offsetOf(file.text, line, character);
    if (offset === null) return null;

    const tokenIdx = findIdentifierTokenAt(file.tokens, offset);
    if (tokenIdx < 0) return null;

    // Walk backwards through identifier/DOT pairs to reconstruct the dotted
    // prefix. Cursor is on the LAST segment we collect — anything after the
    // cursor is ignored so `Inventory|.Reserve` returns "Inventory" only.
    const parts: string[] = [file.tokens[tokenIdx]!.value];
    let startIdx = tokenIdx;
    while (
      startIdx - 2 >= 0 &&
      file.tokens[startIdx - 1]!.type === "DOT" &&
      isNameToken(file.tokens[startIdx - 2]!)
    ) {
      parts.unshift(file.tokens[startIdx - 2]!.value);
      startIdx -= 2;
    }

    const startTok = file.tokens[startIdx]!;
    const endTok = file.tokens[tokenIdx]!;
    return {
      text: parts.join("."),
      span: {
        start: startTok.span.start,
        end: endTok.span.end,
        file: uri,
      },
    };
  }
}

function findIdentifierTokenAt(tokens: readonly Token[], offset: number): number {
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]!;
    if (!isNameToken(tok)) continue;
    if (tok.span.start.offset <= offset && offset <= tok.span.end.offset) {
      return i;
    }
  }
  return -1;
}

function offsetOf(text: string, line: number, character: number): number | null {
  let currentLine = 0;
  let lineStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (currentLine === line) break;
    if (text[i] === "\n") {
      currentLine++;
      lineStart = i + 1;
    }
  }
  if (currentLine < line) return null;
  return lineStart + character;
}
